import { BaseService } from "./BaseService";
import {
    IDataRow, ICommandContextParameter, ICommandExecutionParameter,
    IHierarchicalCommandContextParameter, IODataResult, IGridCommandOption, IGridCommandRequestParameter, IContextParameter,
    ICommandMultipleExecutionParameter, IFormData, IBaseResult, IPageTokenParameter, IExecuteResult,
} from "Entities";
import { CoreContext, QueryParameter, ApplicationParameter } from "Core";
import { Configuration } from "Configuration";

/**
 * Provides methods for accessing the command-specific services on the server.
 * @hidden
 */
export class CommandService extends BaseService {

    /**
     * Create the request parameter for PageToken.
     */
    public static getPageTokenParameter(): IPageTokenParameter {
        let params: IPageTokenParameter = {
            PageToken: CoreContext.Instance.pageToken
        };
        return params;
    }

    /**
     * Creates the request parameters for context.
     */
    public static getContextParameters(): IContextParameter {
        let params: IContextParameter = {
            QueryParameters: null,
            PageToken: null,
            ApplicationParameters: null
        };

        params.QueryParameters = QueryParameter.getAll();

        params.ApplicationParameters = ApplicationParameter.getAll();

        // Set page token
        params.PageToken = CoreContext.Instance.pageToken;

        return params;
    }

    /**
     * Creates the request parameters for context.
     */
    public getContextParameters(): IContextParameter {
        return CommandService.getContextParameters();
    }

    /**
     * Creates the request parameters for access to the Command-Service.
     * Includes the Filter, QueryParameters, ApplicationParameters and the current page token.
     */
    public static getRequestParameters(): ICommandContextParameter {
        let params = <ICommandContextParameter>CommandService.getContextParameters();

        // Load Filter parameters
        params.Filter = CoreContext.getFilterParameters();
        return params;
    }

    /**
     * Performs the get-operation for grid-data.
     * @param url Url to the service.
     * @param gridId ID of the Grid.
     * @param options Request Options (i.e. filter, sort, top, take,...)
     */
    public get(url: string, gridId: number, options: any): Promise<IODataResult> {
        let params: ICommandContextParameter = <ICommandContextParameter>$.extend(CommandService.getRequestParameters(), { options: options });
        return this.post(url + gridId, params);
    }

        /**
     * Performs the get-operation for grid-data.
     * @param url Url to the service.
     * @param gridId ID of the Grid.
     * @param options Request Options (i.e. filter, sort, top, take,...)
     */
    public getFilter(gridId: number, controlId: number, options: any): Promise<IODataResult> {
        let params: ICommandContextParameter = <ICommandContextParameter>$.extend(CommandService.getRequestParameters(), { options: options });
        return this.post(Configuration.baseUrl + "command/getFilter/" + gridId + "/" + controlId, params);
    }

    /**
     * Get data for given form.
     * @param url Configured url for this service.
     * @param gridId GridID of the form.
     */
    public getFormData(url: string, gridId: number): Promise<IFormData> {
        return new Promise<IFormData>((resolve, reject) => {
            let params: ICommandContextParameter = <ICommandContextParameter>$.extend(CommandService.getRequestParameters(), { options: { reloadFromDataSource: true } });
            this.post(url + gridId, params).then((serviceResult: IODataResult) => {
                // Resolve odata result
                if (!serviceResult.error && serviceResult.value && serviceResult.value.length == 1) {
                    resolve(serviceResult.value[0]);
                }
                else {
                    let error = "";
                    if (serviceResult.error) {
                        error = serviceResult.error;
                    } else if (serviceResult.value.length == 0) {
                        error = "No row returned";
                    } else if (serviceResult.value.length > 1) {
                        error = "Too many rows returned";
                    }
                    reject(error);
                }
            });
        });
    }

    /**
     * Performs the get-operation for hierarchical grid-data.
     * @param url Url to the service.
     * @param gridId ID of the Grid.
     * @param rowGuid Guid of the current node.
     * @param parentRowGuid Guid of the parent node.
     */
    public getHierarchical(url: string, gridId: number, rowGuid?: string, parentRowGuid?: string): Promise<IODataResult> {
        let params: IHierarchicalCommandContextParameter = <IHierarchicalCommandContextParameter>CommandService.getRequestParameters();
        if (rowGuid != null) {
            params.__RowGuid = rowGuid;
        }
        if (parentRowGuid != null) {
            params.__ParentRowGuid = parentRowGuid;
        }
        return this.post(url + gridId, params);
    }

    /**
     * Executes a gridCommand.
     * @param gridId ID of the Grid.
     * @param options Options for the gridCommand.
     * @param dataRow Current dataRow.
     */
    public gridCommand(gridId: number, options: IGridCommandOption[], dataRow: IDataRow): Promise<{ [gridCommandId: number]: IODataResult }> {
        let param: IGridCommandRequestParameter = $.extend(CommandService.getRequestParameters(), { DataRow: dataRow, GridCommandOptions: options });
        return this.post(Configuration.baseUrl + "command/gridCommand/" + gridId, param);
    }

    /**
     * Get control data from server.
     * @param gridId ID of the Grid.
     * @param controlId ID of the control.
     */
    public getControl(gridId: number, controlId: number, dataRow: IDataRow): Promise<IODataResult> {
        let params: ICommandContextParameter = <ICommandContextParameter>$.extend(CommandService.getRequestParameters(), { DataRow: dataRow });
        return this.post(`${Configuration.baseUrl}command/get/${gridId}/${controlId}`, params);
    }

    /**
     * Executes a treeLinkCommand.
     * @param treeLinkId ID of the treeLink.
     * @param dataRow Current dataRow.
     */
    public treeLinkCommand(gridId: number, treeLinkId: number, rowGuid?: string, parentRowGuid?: string, fromRowGuid?: string, fromParentRowGuid?: string): Promise<any> {
        let params: IHierarchicalCommandContextParameter = <IHierarchicalCommandContextParameter>CommandService.getRequestParameters();
        if (rowGuid != null) {
            params.__RowGuid = rowGuid;
        }
        if (parentRowGuid != null) {
            params.__ParentRowGuid = parentRowGuid;
        }
        if (fromRowGuid != null) {
            params.__FromRowGuid = fromRowGuid;
        }
        if (fromParentRowGuid != null) {
            params.__FromParentRowGuid = fromParentRowGuid;
        }
        return this.post(Configuration.baseUrl + "command/treeLinkCommand/" + gridId + "/" + treeLinkId, params);
    }

    /**
     * Executes a controlCommand.
     * @param gridId ID of the grid.
     * @param controlId ID of the control.
     * @param dataRow Current dataRow.
     */
    public set(gridId: number, controlId: number, dataRow: IDataRow): Promise<IExecuteResult> {
        let params: ICommandExecutionParameter = <ICommandExecutionParameter>$.extend(CommandService.getRequestParameters(), { DataRow: dataRow });
        return this.post(Configuration.baseUrl + "command/set/" + gridId + "/" + controlId, params);
    }

    /**
     * Executes a controlCommand.
     * @param gridId ID of the grid.
     * @param controlId ID of the control.
     * @param dataRow Current dataRow.
     */
    public setMultiple(gridId: number, controlId: number, dataRows: IDataRow[]): Promise<IBaseResult> {
        let param: ICommandMultipleExecutionParameter = {
            DataRows: dataRows
        };

        let params: ICommandMultipleExecutionParameter = <ICommandMultipleExecutionParameter>$.extend(CommandService.getRequestParameters(), param);
        return this.post(Configuration.baseUrl + "command/setMultiple/" + gridId + "/" + controlId, params);
    }

     /**
     * Executes a controlCommand.
     * @param gridId ID of the grid.
     * @param controlId ID of the control.
     * @param dataRow Current dataRow.
     */
    public setDataset(gridId: number, controlId: number, options: any): Promise<IBaseResult> {
        let params: ICommandContextParameter = <ICommandContextParameter>$.extend(CommandService.getRequestParameters(), { options: options });

        return this.post(Configuration.baseUrl + "command/setDataset/" + gridId + "/" + controlId, params);
    }
}
